import type { ApiMode } from "./contracts";

export interface RuntimeEnvironment {
  DEV?: boolean;
  PROD?: boolean;
  VITE_API_MODE?: string;
  VITE_API_BASE_URL?: string;
  VITE_ALLOW_NON_RELEASE_SOURCE_DRAFTS?: string;
  VITE_SOURCE_DRAFT_TEACHER_MODE?: string;
}

export type RuntimeContentMode =
  | "release"
  | "demo"
  | "non_release_source_drafts";

export interface RuntimeConfig {
  apiMode: ApiMode;
  apiBaseUrl: string;
  isExplicitDemo: boolean;
  contentMode: RuntimeContentMode;
  allowsNonReleaseSourceDrafts: boolean;
  sourceDraftTeacherMode: "deterministic" | "live" | null;
}

export function resolveRuntimeConfig(
  environment: RuntimeEnvironment,
): RuntimeConfig {
  const requestedMode = environment.VITE_API_MODE?.trim().toLowerCase();
  if (
    requestedMode !== undefined &&
    requestedMode !== "demo" &&
    requestedMode !== "http"
  ) {
    throw new Error(
      `Unsupported VITE_API_MODE "${requestedMode}". Use "demo" or "http".`,
    );
  }

  const apiMode: ApiMode =
    requestedMode === "demo" || requestedMode === "http"
      ? requestedMode
      : "http";
  const sourceDraftSetting =
    environment.VITE_ALLOW_NON_RELEASE_SOURCE_DRAFTS?.trim().toLowerCase();
  if (
    sourceDraftSetting !== undefined &&
    sourceDraftSetting !== "true" &&
    sourceDraftSetting !== "false"
  ) {
    throw new Error(
      "VITE_ALLOW_NON_RELEASE_SOURCE_DRAFTS must be true or false.",
    );
  }
  const allowsNonReleaseSourceDrafts = sourceDraftSetting === "true";
  if (
    allowsNonReleaseSourceDrafts &&
    (environment.DEV !== true ||
      environment.PROD === true ||
      apiMode !== "http")
  ) {
    throw new Error(
      "Non-release source drafts require an explicit development HTTP runtime.",
    );
  }
  const sourceDraftTeacherMode =
    environment.VITE_SOURCE_DRAFT_TEACHER_MODE?.trim().toLowerCase();
  if (
    sourceDraftTeacherMode !== undefined &&
    sourceDraftTeacherMode !== "deterministic" &&
    sourceDraftTeacherMode !== "live"
  ) {
    throw new Error(
      "VITE_SOURCE_DRAFT_TEACHER_MODE must be deterministic or live.",
    );
  }
  if (allowsNonReleaseSourceDrafts && sourceDraftTeacherMode === undefined) {
    throw new Error(
      "Non-release source drafts require an explicit teacher mode.",
    );
  }
  if (!allowsNonReleaseSourceDrafts && sourceDraftTeacherMode !== undefined) {
    throw new Error(
      "VITE_SOURCE_DRAFT_TEACHER_MODE is valid only for non-release source drafts.",
    );
  }

  return {
    apiMode,
    apiBaseUrl:
      environment.VITE_API_BASE_URL?.replace(/\/+$/, "") ??
      "http://127.0.0.1:3210",
    isExplicitDemo: requestedMode === "demo",
    contentMode:
      apiMode === "demo"
        ? "demo"
        : allowsNonReleaseSourceDrafts
          ? "non_release_source_drafts"
          : "release",
    allowsNonReleaseSourceDrafts,
    sourceDraftTeacherMode: sourceDraftTeacherMode ?? null,
  };
}
